/**
 * The sleep timer menu.
 *
 * Anchored `bottom-full`: it opens from a button in the bottom player bar, so
 * it has to grow upward, unlike a header-anchored popover.
 */

import { useEffect, useRef } from "react";

import { CloseIcon } from "../components/icons";
import { IconButton, clock } from "../components/ui";
import { t, useUiLang } from "../i18n";
import {
  SLEEP_CHAPTER_COUNTS,
  SLEEP_DURATIONS,
  type SleepMode,
  type SleepPick,
  type SleepState,
} from "../lib/sleepTimer";

type Props = { className?: string };

/** Same stroke recipe as `components/icons.tsx` (20px grid, round caps), kept
 * local because this file does not own that shared module. */
function svg(path: React.ReactNode) {
  return function Icon({ className = "h-4 w-4" }: Props) {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
      >
        {path}
      </svg>
    );
  };
}

const StopIcon = svg(<rect x="5" y="5" width="10" height="10" rx="1.5" />);
const PlusIcon = svg(
  <>
    <path d="M10 4.5v11" />
    <path d="M4.5 10h11" />
  </>,
);

/** What the status row says while a mode other than "off" is armed. */
function modeLabel(mode: SleepMode): string {
  switch (mode.kind) {
    case "off":
      return "";
    case "duration":
      return t("sleepStoppingIn");
    case "endOfChapter":
      return t("sleepUntilChapterEnds");
    case "chapters":
      return mode.left > 1
        ? t("sleepChaptersLeft", { n: mode.left })
        : t("sleepLastChapter");
  }
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "tap flex items-center justify-center rounded-lg border px-2 py-1.5 text-sm transition-colors duration-150 " +
        (active
          ? "border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
          : "border-[var(--color-line)] text-[var(--color-ink)] hover:bg-[var(--color-raised)]")
      }
    >
      {children}
    </button>
  );
}

export function SleepMenu({
  open,
  onClose,
  state,
  onPick,
  onExtend,
}: {
  open: boolean;
  onClose: () => void;
  state: SleepState;
  onPick: (choice: SleepPick) => void;
  onExtend: () => void;
}) {
  useUiLang();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!panel.current?.contains(event.target as Node)) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    // Deferred: the click that opened the menu must not immediately close it.
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", away);
      document.addEventListener("keydown", escape);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open, onClose]);

  if (!open) return null;

  const pick = (choice: SleepPick) => {
    onPick(choice);
    onClose();
  };

  const active = state.mode.kind !== "off";

  return (
    <div
      ref={panel}
      role="dialog"
      aria-label={t("sleepTitle")}
      className={
        "absolute right-0 bottom-full z-30 mb-2 w-72 max-w-[calc(100vw-1.5rem)] rounded-xl bg-[var(--color-raised)] p-4 " +
        "shadow-2xl shadow-black/50 ring-1 ring-[var(--color-line)]"
      }
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-medium text-[var(--color-ink)]">{t("sleepTitle")}</h2>
        <IconButton label={t("sleepClose")} onClick={onClose}>
          <CloseIcon className="h-4 w-4" />
        </IconButton>
      </div>

      {active && (
        <div className="mb-4 flex items-center justify-between gap-2 rounded-lg bg-[var(--color-surface)] px-3 py-2">
          <span
            className={
              "flex items-baseline gap-2 text-sm " +
              (state.fading ? "text-[var(--color-accent)]" : "text-[var(--color-ink)]")
            }
          >
            {modeLabel(state.mode)}
            {state.remaining !== null && (
              <span className="font-mono text-sm tabular-nums">{clock(state.remaining)}</span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <IconButton
              label={t("sleepAddMore")}
              onClick={() => {
                onExtend();
                onClose();
              }}
            >
              <PlusIcon />
            </IconButton>
            <IconButton label={t("sleepStop")} onClick={() => pick({ kind: "off" })}>
              <StopIcon />
            </IconButton>
          </span>
        </div>
      )}

      <div className="grid gap-3">
        <div>
          <span className="text-xs text-[var(--color-muted)]">{t("sleepStopAfter")}</span>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {SLEEP_DURATIONS.map((minutes) => (
              <ModeButton
                key={minutes}
                active={state.mode.kind === "duration" && state.mode.minutes === minutes}
                onClick={() => pick({ kind: "duration", minutes })}
              >
                {t("sleepMinutes", { minutes })}
              </ModeButton>
            ))}
          </div>
        </div>

        <ModeButton
          active={state.mode.kind === "endOfChapter"}
          onClick={() => pick({ kind: "endOfChapter" })}
        >
          {t("sleepEndOfChapter")}
        </ModeButton>

        <div>
          <span className="text-xs text-[var(--color-muted)]">{t("sleepChaptersGroup")}</span>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {SLEEP_CHAPTER_COUNTS.map((count) => (
              <ModeButton
                key={count}
                active={state.mode.kind === "chapters" && state.mode.total === count}
                onClick={() => pick({ kind: "chapters", count })}
              >
                {t(count > 1 ? "sleepChapterCountMany" : "sleepChapterCountOne", { count })}
              </ModeButton>
            ))}
          </div>
        </div>

        {active && (
          <ModeButton onClick={() => pick({ kind: "off" })}>{t("sleepOff")}</ModeButton>
        )}
      </div>
    </div>
  );
}
